import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Card,
  Typography,
  Grid,
  Collapse,
  Alert,
  Button,
  TextField,
  useTheme
} from '@mui/material';
import {
  CalendarToday as CalendarTodayIcon,
  Business as BusinessIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { RenewalDashboardFilters, RenewalDashboardFilterOptions } from '@/types/renewalDashboard';
import { FilterSectionHeader } from '@/components/filters/FilterSectionHeader';
import { FilterSectionGroup } from '@/components/filters/FilterSectionGroup';
import { AppliedFiltersChips, FilterChip } from '@/components/filters/AppliedFiltersChips';
import { MultiSelect } from '@/components/ui/MultiSelect';
import { SingleSelect } from '@/components/ui/SingleSelect';
import { CHOLA_COLORS } from '@/constants/filterStyles';
import { getRenewalDashboardDependentFilters } from '@/services/api';
import { formatErrorForDisplay } from '@/utils/errorUtils';

interface RenewalFiltersPanelProps {
  filters: RenewalDashboardFilters | null;
  loading?: boolean;
  error?: string | null;
  onFiltersChange: (filters: RenewalDashboardFilterOptions) => void;
  onClearFilters: () => void;
}

const RenewalFiltersPanel: React.FC<RenewalFiltersPanelProps> = ({
  filters,
  loading = false,
  error = null,
  onFiltersChange,
  onClearFilters
}) => {
  const theme = useTheme();
  const [isExpanded, setIsExpanded] = useState(false); // Collapsed by default to show filter bar
  const [localFilters, setLocalFilters] = useState<RenewalDashboardFilterOptions>({});
  const [redDate, setRedDate] = useState<Date | null>(null);
  
  // Sync RED date from filters
  useEffect(() => {
    if (localFilters.red_from) {
      const [year, month, day] = localFilters.red_from.split('-').map(Number);
      setRedDate(new Date(year, month - 1, day));
    } else if (localFilters.red_to) {
      const [year, month, day] = localFilters.red_to.split('-').map(Number);
      setRedDate(new Date(year, month - 1, day));
    } else {
      setRedDate(null);
    }
  }, [localFilters.red_from, localFilters.red_to]);

  // Validate and clear RED date when RED Month changes
  useEffect(() => {
    if (localFilters.month && redDate) {
      const monthRange = getMonthDateRange(localFilters.month);
      if (monthRange) {
        // Check if current RED date is outside the selected month range
        if (redDate < monthRange.minDate || redDate > monthRange.maxDate) {
          // Clear RED date if it's outside the month range
          const newFilters = { ...localFilters };
          delete newFilters.red_from;
          delete newFilters.red_to;
          setRedDate(null);
          setLocalFilters(newFilters);
        }
      }
    } else if (localFilters.month && !redDate) {
      // If month is selected but no RED date, ensure filters are clean
      const newFilters = { ...localFilters };
      if (newFilters.red_from || newFilters.red_to) {
        delete newFilters.red_from;
        delete newFilters.red_to;
        setLocalFilters(newFilters);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localFilters.month, redDate]);
  
  // State for dependent filters (cascading)
  const [dependentSubChannels, setDependentSubChannels] = useState<string[]>([]);
  const [dependentSubChannelsLoading, setDependentSubChannelsLoading] = useState(false);
  const [dependentChannels, setDependentChannels] = useState<string[]>([]);
  const [dependentChannelsLoading, setDependentChannelsLoading] = useState(false);
  const [dependentProductNames, setDependentProductNames] = useState<string[]>([]);
  const [dependentProductNamesLoading, setDependentProductNamesLoading] = useState(false);
  
  // Helper function to normalize filter value to array (handle backward compatibility)
  const normalizeToArray = (value: string | string[] | undefined): string[] => {
    if (!value) return [];
    if (Array.isArray(value)) return value;
    return [value];
  };

  // Get current filter values as arrays
  const channelArray = normalizeToArray(localFilters.channel);
  const subChannelArray = normalizeToArray(localFilters.sub_channel);
  const lobArray = normalizeToArray(localFilters.lob);
  const productNameArray = normalizeToArray(localFilters.product_name);

  // Watch for LOB changes and fetch dependent product names
  useEffect(() => {
    if (lobArray.length > 0) {
      setDependentProductNamesLoading(true);
      getRenewalDashboardDependentFilters(
        undefined, // channel
        undefined, // sub_channel
        lobArray.length === 1 ? lobArray[0] : lobArray
      )
        .then((response) => {
          setDependentProductNames(response.product_names || []);
          setDependentProductNamesLoading(false);
        })
        .catch((error) => {
          console.error('Error fetching dependent product names:', error);
          setDependentProductNames([]);
          setDependentProductNamesLoading(false);
        });
    } else {
      setDependentProductNames([]);
      setDependentProductNamesLoading(false);
    }
  }, [lobArray.join(',')]);
  
  // Watch for product name changes and fetch dependent channels
  useEffect(() => {
    if (productNameArray.length > 0) {
      setDependentChannelsLoading(true);
      getRenewalDashboardDependentFilters(
        undefined, // channel
        undefined, // sub_channel
        undefined, // lob
        productNameArray.length === 1 ? productNameArray[0] : productNameArray
      )
        .then((response) => {
          setDependentChannels(response.channels || []);
          setDependentChannelsLoading(false);
        })
        .catch((error) => {
          console.error('Error fetching dependent channels:', error);
          setDependentChannels([]);
          setDependentChannelsLoading(false);
        });
    } else {
      setDependentChannels([]);
      setDependentChannelsLoading(false);
    }
  }, [productNameArray.join(',')]);
  
  // Watch for channel changes and fetch dependent sub-channels
  useEffect(() => {
    if (channelArray.length > 0) {
      setDependentSubChannelsLoading(true);
      getRenewalDashboardDependentFilters(
        channelArray.length === 1 ? channelArray[0] : channelArray
      )
        .then((response) => {
          setDependentSubChannels(response.sub_channels || []);
          setDependentSubChannelsLoading(false);
        })
        .catch((error) => {
          console.error('Error fetching dependent sub-channels:', error);
          setDependentSubChannels([]);
          setDependentSubChannelsLoading(false);
        });
    } else {
      setDependentSubChannels([]);
      setDependentSubChannelsLoading(false);
    }
  }, [channelArray.join(',')]);
  
  // Clear invalid product_name selections when dependent product names change
  useEffect(() => {
    if (productNameArray.length > 0 && dependentProductNames.length > 0) {
      const validProductNames = productNameArray.filter(name => dependentProductNames.includes(name));
      if (validProductNames.length !== productNameArray.length) {
        const newFilters = { ...localFilters };
        newFilters.product_name = validProductNames.length > 0 ? (validProductNames.length === 1 ? validProductNames[0] : validProductNames) : undefined;
        setLocalFilters(newFilters);
      }
    }
  }, [dependentProductNames.join(','), productNameArray.join(',')]);
  
  // Clear invalid channel selections when dependent channels change
  useEffect(() => {
    if (channelArray.length > 0 && dependentChannels.length > 0) {
      const validChannels = channelArray.filter(ch => dependentChannels.includes(ch));
      if (validChannels.length !== channelArray.length) {
        const newFilters = { ...localFilters };
        newFilters.channel = validChannels.length > 0 ? (validChannels.length === 1 ? validChannels[0] : validChannels) : undefined;
        // Also clear sub_channel if channel changes
        if (newFilters.channel !== localFilters.channel) {
          newFilters.sub_channel = undefined;
        }
        setLocalFilters(newFilters);
      }
    }
  }, [dependentChannels.join(','), channelArray.join(',')]);
  
  // Clear invalid sub_channel selections when dependent sub-channels change
  useEffect(() => {
    if (subChannelArray.length > 0 && dependentSubChannels.length > 0) {
      const validSubChannels = subChannelArray.filter(sc => dependentSubChannels.includes(sc));
      if (validSubChannels.length !== subChannelArray.length) {
        const newFilters = { ...localFilters };
        newFilters.sub_channel = validSubChannels.length > 0 ? (validSubChannels.length === 1 ? validSubChannels[0] : validSubChannels) : undefined;
        setLocalFilters(newFilters);
      }
    }
  }, [dependentSubChannels.join(','), subChannelArray.join(',')]);

  const handleFilterChange = (field: keyof RenewalDashboardFilterOptions) => (
    event: any
  ) => {
    const value = event.target ? event.target.value : event;
    setLocalFilters(prev => ({
      ...prev,
      [field]: value || undefined
    }));
  };

  const handleSingleSelectChange = (field: keyof RenewalDashboardFilterOptions) => (
    selected: { value: string; label: string } | null
  ) => {
    const newFilters = { ...localFilters };
    const value = selected && selected.value !== '' ? selected.value : undefined;
    
    // Auto-fill RED from/to when date_range is selected
    if (field === 'date_range' && value && value !== 'custom') {
      const dateRange = calculateDateRange(value);
      if (dateRange) {
        // Use local date formatting to avoid timezone issues
        newFilters.red_from = formatDateLocal(dateRange.startDate);
        newFilters.red_to = formatDateLocal(dateRange.endDate);
        setRedDate(dateRange.startDate);
      }
    } else if (field === 'date_range' && (!value || value === 'custom')) {
      // Clear RED dates when date_range is cleared/reset
      delete newFilters.red_from;
      delete newFilters.red_to;
      setRedDate(null);
    }
    
    newFilters[field] = value;
    setLocalFilters(newFilters);
  };

  // Handler for MultiSelect changes (extracts array from selected options)
  const handleMultiSelectChange = (field: 'channel' | 'sub_channel' | 'lob' | 'product_name') => (
    selected: { value: string; label: string }[]
  ) => {
    const newFilters = { ...localFilters };
    const values = selected.map(opt => opt.value);
    const isEmpty = values.length === 0;
    
    // Handle cascading filter clearing based on new dependency chain: LOB → Product → Channel → Sub Channel
    if (field === 'lob' && isEmpty) {
      // If LOB is being cleared, also clear product_name, channel, and sub_channel
      delete newFilters.product_name;
      delete newFilters.channel;
      delete newFilters.sub_channel;
    } else if (field === 'lob' && !isEmpty) {
      // If LOB is being changed (not cleared), clear downstream filters
      delete newFilters.product_name;
      delete newFilters.channel;
      delete newFilters.sub_channel;
    } else if (field === 'product_name' && isEmpty) {
      // If product_name is being cleared, also clear channel and sub_channel
      delete newFilters.channel;
      delete newFilters.sub_channel;
    } else if (field === 'product_name' && !isEmpty) {
      // If product_name is being changed (not cleared), clear downstream filters
      delete newFilters.channel;
      delete newFilters.sub_channel;
    } else if (field === 'channel' && isEmpty) {
      // If channel is being cleared, also clear sub_channel
      delete newFilters.sub_channel;
    } else if (field === 'channel' && !isEmpty) {
      // If channel is being changed (not cleared), clear sub_channel
      delete newFilters.sub_channel;
    }
    
    newFilters[field] = values.length > 0 ? (values.length === 1 ? values[0] : values) : undefined;
    setLocalFilters(newFilters);
  };

  // Helper function to format date as YYYY-MM-DD using local time (avoid timezone issues)
  const formatDateLocal = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Calculate date range from T-X/T+X format
  // Note: T-X means X days FROM today (future), T+X means X days BEFORE today (past)
  // This matches the database days_offset logic where positive = future, negative = past
  const calculateDateRange = (dateRange: string): { startDate: Date; endDate: Date } | null => {
    if (!dateRange || dateRange === 'custom') return null;
    
    const match = dateRange.match(/^T([+-])(\d+)$/);
    if (!match) return null;
    
    const operator = match[1];
    const days = parseInt(match[2], 10);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    if (operator === '-') {
      // T-X means RED = X days FROM today (future) - matches database days_offset = +X
      const targetDate = new Date(today);
      targetDate.setDate(today.getDate() + days);
      return { startDate: targetDate, endDate: targetDate };
    } else {
      // T+X means RED = X days BEFORE today (past) - matches database days_offset = -X
      const targetDate = new Date(today);
      targetDate.setDate(today.getDate() - days);
      return { startDate: targetDate, endDate: targetDate };
    }
  };

  // Parse RED Month string (format: "DEC-2025" or "DECEMBER-2025")
  const parseMonthString = (month: string): { year: number; month: number } | null => {
    if (!month) return null;
    
    // Month abbreviation to number mapping
    const monthMap: { [key: string]: number } = {
      'JAN': 1, 'JANUARY': 1,
      'FEB': 2, 'FEBRUARY': 2,
      'MAR': 3, 'MARCH': 3,
      'APR': 4, 'APRIL': 4,
      'MAY': 5,
      'JUN': 6, 'JUNE': 6,
      'JUL': 7, 'JULY': 7,
      'AUG': 8, 'AUGUST': 8,
      'SEP': 9, 'SEPTEMBER': 9,
      'OCT': 10, 'OCTOBER': 10,
      'NOV': 11, 'NOVEMBER': 11,
      'DEC': 12, 'DECEMBER': 12
    };
    
    // Split by hyphen or space
    const parts = month.split(/[- ]/);
    if (parts.length < 2) return null;
    
    const monthStr = parts[0].toUpperCase().trim();
    const yearStr = parts[1].trim();
    
    const monthNum = monthMap[monthStr];
    const year = parseInt(yearStr, 10);
    
    if (!monthNum || isNaN(year)) return null;
    
    return { year, month: monthNum };
  };

  // Get date range for a given month string
  const getMonthDateRange = (month: string): { minDate: Date; maxDate: Date } | null => {
    const parsed = parseMonthString(month);
    if (!parsed) return null;
    
    // Create minDate: first day of the month at 00:00:00
    const minDate = new Date(parsed.year, parsed.month - 1, 1, 0, 0, 0, 0);
    
    // Create maxDate: last day of the month at 23:59:59
    const maxDate = new Date(parsed.year, parsed.month, 0, 23, 59, 59, 999);
    
    return { minDate, maxDate };
  };

  const handleRedDateChange = (date: Date | null) => {
    setRedDate(date);
    const newFilters = { ...localFilters };
    if (date) {
      // Set both red_from and red_to to the same date (exact match)
      const dateStr = formatDateLocal(date);
      newFilters.red_from = dateStr;
      newFilters.red_to = dateStr;
    } else {
      // Clear RED dates
      delete newFilters.red_from;
      delete newFilters.red_to;
    }
    setLocalFilters(newFilters);
  };

  // Dedicated handler to clear RED date filter
  const handleClearRedDate = useCallback(() => {
    const newFilters = { ...localFilters };
    delete newFilters.red_from;
    delete newFilters.red_to;
    setRedDate(null);
    setLocalFilters(newFilters);
  }, [localFilters]);

  const applyFilters = () => {
    onFiltersChange(localFilters);
  };

  const clearFilters = () => {
    setLocalFilters({});
    onClearFilters();
  };

  const getActiveFiltersCount = useCallback((): number => {
    let count = 0;
    if (localFilters.month) count++;
    if (localFilters.date_range) count++;
    if (localFilters.red_from || localFilters.red_to) count++;
    if (channelArray.length > 0) count++;
    if (subChannelArray.length > 0) count++;
    if (lobArray.length > 0) count++;
    if (productNameArray.length > 0) count++;
    if (localFilters.policy_number) count++;
    if (localFilters.customer_mobile) count++;
    return count;
  }, [localFilters, channelArray.length, subChannelArray.length, lobArray.length, productNameArray.length]);

  // Generate applied filter chips
  const appliedFilterChips = useMemo((): FilterChip[] => {
    const chips: FilterChip[] = [];
    
    // 1. Non-dependent filters first: Month, Date Range, RED
    if (localFilters.month) {
      chips.push({
        key: 'month',
        label: `Month: ${localFilters.month}`,
        onDelete: () => {
          const newFilters = { ...localFilters };
          delete newFilters.month;
          setLocalFilters(newFilters);
        }
      });
    }
    if (localFilters.date_range) {
      // Use display_label from database (filters.date_ranges) if available
      const dateRangeObj = filters?.date_ranges?.find((dr: { id: string; name: string }) => dr.id === localFilters.date_range);
      const displayLabel = dateRangeObj?.name || localFilters.date_range;
      chips.push({
        key: 'date_range',
        label: `Date Range: ${displayLabel}`,
        onDelete: () => {
          const newFilters = { ...localFilters };
          delete newFilters.date_range;
          setLocalFilters(newFilters);
        }
      });
    }
    if (localFilters.red_from || localFilters.red_to) {
      const dateStr = localFilters.red_from || localFilters.red_to || '';
      chips.push({
        key: 'red_date',
        label: `RED: ${dateStr}`,
        onDelete: handleClearRedDate
      });
    }

    // 2. Dependent filters: collect chips separately so we can control order
    const lobChips: FilterChip[] = [];
    const productChips: FilterChip[] = [];
    const channelChips: FilterChip[] = [];
    const subChannelChips: FilterChip[] = [];

    // LOB chips
    if (lobArray.length > 0) {
      const labelValue = lobArray.length === 1 ? lobArray[0] : lobArray.join(', ');
      lobChips.push({
        key: 'lob',
        label: `LOB: ${labelValue}`,
        onDelete: () => {
          const newFilters = { ...localFilters };
          delete newFilters.lob;
          setLocalFilters(newFilters);
        }
      });
    }

    // Product chips
    if (productNameArray.length > 0) {
      const labelValue = productNameArray.length === 1 ? productNameArray[0] : productNameArray.join(', ');
      productChips.push({
        key: 'product_name',
        label: `Product: ${labelValue}`,
        onDelete: () => {
          const newFilters = { ...localFilters };
          delete newFilters.product_name;
          setLocalFilters(newFilters);
        }
      });
    }

    // Channel chips
    if (channelArray.length > 0) {
      const labelValue = channelArray.length === 1 ? channelArray[0] : channelArray.join(', ');
      channelChips.push({
        key: 'channel',
        label: `Channel: ${labelValue}`,
        onDelete: () => {
          const newFilters = { ...localFilters };
          delete newFilters.channel;
          setLocalFilters(newFilters);
        }
      });
    }

    // Sub Channel chips
    if (subChannelArray.length > 0) {
      const labelValue = subChannelArray.length === 1 ? subChannelArray[0] : subChannelArray.join(', ');
      subChannelChips.push({
        key: 'sub_channel',
        label: `Sub Channel: ${labelValue}`,
        onDelete: () => {
          const newFilters = { ...localFilters };
          delete newFilters.sub_channel;
          setLocalFilters(newFilters);
        }
      });
    }

    // 3. Push dependent chips in strict order: LOB → Product → Channel → Sub Channel
    chips.push(...lobChips, ...productChips, ...channelChips, ...subChannelChips);

    // 4. Remaining filters: Policy Number, Customer Mobile
    if (localFilters.policy_number) {
      chips.push({
        key: 'policy_number',
        label: `Policy: ${localFilters.policy_number}`,
        onDelete: () => {
          const newFilters = { ...localFilters };
          delete newFilters.policy_number;
          setLocalFilters(newFilters);
        }
      });
    }
    if (localFilters.customer_mobile) {
      chips.push({
        key: 'customer_mobile',
        label: `Mobile: ${localFilters.customer_mobile}`,
        onDelete: () => {
          const newFilters = { ...localFilters };
          delete newFilters.customer_mobile;
          setLocalFilters(newFilters);
        }
      });
    }
    
    return chips;
  }, [localFilters, channelArray, subChannelArray, lobArray, productNameArray, filters, handleClearRedDate]);

  const hasActiveFilters = getActiveFiltersCount() > 0;

  if (loading) {
    return (
      <Card>
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}>
          <Typography variant="body2">
            Loading filter options...
          </Typography>
        </Box>
      </Card>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        {formatErrorForDisplay(error)}
      </Alert>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Card 
        sx={{ 
          mb: 2,
          mt: 0,
          transition: theme.transitions.create(['box-shadow']),
          '&:hover': {
            boxShadow: isExpanded ? theme.shadows[4] : theme.shadows[1],
          }
        }}
      >
        <FilterSectionHeader
          expanded={isExpanded}
          onToggle={() => setIsExpanded(!isExpanded)}
          appliedCount={getActiveFiltersCount()}
          onClearAll={clearFilters}
          title="Filters"
          compact
        />

        {/* Applied Filters Display */}
        {hasActiveFilters && !isExpanded && (
          <AppliedFiltersChips chips={appliedFilterChips} />
        )}

        <Collapse in={isExpanded}>
          <Box sx={{ p: 1, bgcolor: 'background.paper' }}>
            <Grid container spacing={0.75}>
              {/* Top Filters Row - keep critical filters high on the page to avoid overlap with modal */}
              <Grid item xs={12} sm={6} md={3}>
                <SingleSelect
                  compact
                  label="RED Month"
                  options={filters?.months?.map((month) => ({
                    value: month,
                    label: month
                  })) || []}
                  value={localFilters.month ? { value: localFilters.month, label: localFilters.month } : null}
                  onChange={handleSingleSelectChange('month')}
                  placeholder="Select month..."
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <SingleSelect
                  compact
                  label="Date Range"
                  options={
                    filters?.date_ranges
                      ? filters.date_ranges
                          .filter((dr: { id: string; name: string }) => dr.id !== 'custom')
                          .map((dr: { id: string; name: string }) => ({
                            value: dr.id,
                            label: dr.name
                          }))
                      : []
                  }
                  value={
                    localFilters.date_range
                      ? {
                          value: localFilters.date_range,
                          label: filters?.date_ranges?.find((dr: { id: string; name: string }) => dr.id === localFilters.date_range)?.name || localFilters.date_range
                        }
                      : null
                  }
                  onChange={handleSingleSelectChange('date_range')}
                  placeholder="Select date range..."
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ width: '100%' }}>
                  <Typography 
                    variant="body2" 
                    color="text.secondary" 
                    sx={{ 
                      mb: 0.5, 
                      fontWeight: 500, 
                      fontSize: '0.8125rem' 
                    }}
                  >
                    RED
                  </Typography>
                  <DatePicker
                    label=""
                    value={redDate}
                    onChange={handleRedDateChange}
                    format="dd-MMM-yyyy"
                    disabled={!!localFilters.date_range && localFilters.date_range !== 'custom'}
                    minDate={localFilters.month ? getMonthDateRange(localFilters.month)?.minDate : undefined}
                    maxDate={localFilters.month ? getMonthDateRange(localFilters.month)?.maxDate : undefined}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        size: 'small',
                        sx: {
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '8px',
                            minHeight: 32,
                            fontSize: '0.8125rem',
                          },
                          '& .MuiInputLabel-root': {
                            display: 'none',
                          },
                        },
                      },
                    }}
                  />
                </Box>
              </Grid>

              {/* Business Filters Section */}
              <Grid item xs={12}>
                <FilterSectionGroup
                  title="Business Filters"
                  icon={BusinessIcon}
                  compact
                >
                  <Grid item xs={12} sm={6} md={3}>
                    <MultiSelect
                      compact
                      label="Line of Business (LOB)"
                      options={filters?.lobs?.map((lob) => ({
                        value: lob,
                        label: lob
                      })) || []}
                      value={lobArray.map(lob => ({ value: lob, label: lob }))}
                      onChange={handleMultiSelectChange('lob')}
                      placeholder="Select LOBs..."
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <MultiSelect
                      compact
                      label="Product Name"
                      options={dependentProductNames.map((name) => ({
                        value: name,
                        label: name
                      }))}
                      value={productNameArray.map(name => ({ value: name, label: name }))}
                      onChange={handleMultiSelectChange('product_name')}
                      placeholder={lobArray.length > 0 ? 'Select product names...' : 'Select LOB first'}
                      isLoading={dependentProductNamesLoading}
                      disabled={lobArray.length === 0}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <MultiSelect
                      compact
                      label="Channel"
                      options={dependentChannels.map((channel) => ({
                        value: channel,
                        label: channel
                      }))}
                      value={channelArray.map(ch => ({ value: ch, label: ch }))}
                      onChange={handleMultiSelectChange('channel')}
                      placeholder={productNameArray.length > 0 ? 'Select channels...' : 'Select product first'}
                      isLoading={dependentChannelsLoading}
                      disabled={productNameArray.length === 0}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <MultiSelect
                      compact
                      label="Sub Channel"
                      options={dependentSubChannels.map((subChannel) => ({
                        value: subChannel,
                        label: subChannel
                      }))}
                      value={subChannelArray.map(sc => ({ value: sc, label: sc }))}
                      onChange={handleMultiSelectChange('sub_channel')}
                      placeholder={channelArray.length > 0 ? 'Select sub channels...' : 'Select channel first'}
                      isLoading={dependentSubChannelsLoading}
                      disabled={channelArray.length === 0}
                    />
                  </Grid>
                </FilterSectionGroup>
            </Grid>

              {/* Additional Filters Section */}
              <Grid item xs={12}>
                <Box sx={{ mb: 1 }}>
                  <Typography variant="subtitle2" sx={{ 
                    mb: 0.5, 
                    color: CHOLA_COLORS.primary,
                    fontWeight: 500,
                    fontSize: '0.65rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5
                  }}>
                    Additional Filters
                  </Typography>
                  <Grid container spacing={0.5}>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        label="Policy Number"
                        placeholder="Enter policy number"
                        value={localFilters.policy_number || ''}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          const newFilters = { ...localFilters };
                          newFilters.policy_number = e.target.value || undefined;
                          setLocalFilters(newFilters);
                        }}
                        fullWidth
                        size="small"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '8px',
                            minHeight: 32,
                            fontSize: '0.8125rem',
                          },
                          '& .MuiInputLabel-root': {
                            fontSize: '0.8125rem',
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        label="Customer Mobile"
                        placeholder="Enter mobile number"
                        value={localFilters.customer_mobile || ''}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          const newFilters = { ...localFilters };
                          newFilters.customer_mobile = e.target.value || undefined;
                          setLocalFilters(newFilters);
                        }}
                        fullWidth
                        size="small"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '8px',
                            minHeight: 32,
                            fontSize: '0.8125rem',
                          },
                          '& .MuiInputLabel-root': {
                            fontSize: '0.8125rem',
                          },
                        }}
                      />
                    </Grid>
          </Grid>
                </Box>
              </Grid>

              {/* Apply Filters Button Section */}
              <Grid item xs={12}>
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'flex-end', 
                  mt: 0.5,
                  borderTop: `1px solid ${theme.palette.divider}`,
                  pt: 0.5
                }}>
                  <Button 
                    variant="contained" 
                    color="primary"
                    onClick={applyFilters}
                    startIcon={<SearchIcon />}
                    sx={{
                      backgroundColor: CHOLA_COLORS.primary,
                      fontSize: '0.7rem',
                      py: 0.4,
                      px: 1,
                      minHeight: '32px',
                      '&:hover': {
                        backgroundColor: CHOLA_COLORS.hover
                      }
                    }}
                  >
                    Search
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Collapse>
      </Card>
    </LocalizationProvider>
  );
};

export default RenewalFiltersPanel;
